/**
 * SearchAnalyticsFunction.ts
 * Common analytics functions for search components
 */
import { headerConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";

/**
 * Handle search back button click analytics
 * Logs click event for the search back button icon
 */
export const handleSearchBackIconClickDL = (
  searchKeyword: string,
  ctaTextOrCmp: string | boolean = headerConstants.ELEMENTS.SEARCH_BACK_ICON,
  cmpOrSubTitle: boolean | string = false,
  position: number = 0,
  subTitle: string = ""
) => {
  const trimmedKeyword = searchKeyword.trim();
  const isProdSignature = typeof ctaTextOrCmp === "boolean";
  const cta_text = isProdSignature
    ? headerConstants.ELEMENTS.SEARCH_BACK_ICON
    : ctaTextOrCmp;
  const cmp = isProdSignature ? ctaTextOrCmp : Boolean(cmpOrSubTitle);
  const resolvedSubTitle = isProdSignature
    ? String(cmpOrSubTitle || "")
    : subTitle;

  const data = {
    component: cmp
      ? headerConstants.COMPONENTS.SEARCH_RESULT_PAGE
      : headerConstants.COMPONENTS.SEARCH_BANNER,
    event_click: headerConstants.SEARCH.EVENT_CLICK.ICON_CLICK,
    target: cmp
      ? headerConstants.SEARCH.TARGET.SEARCH_RESULT_REMOVE_OPTION
      : headerConstants.SEARCH.TARGET.SEARCH_BANNER_DIRECTION_ICON,
    cta_text: cta_text,
    search_keyword: trimmedKeyword,
    index: position !== 0 ? position : "NA",
    sub_section: headerConstants.SEARCH.SUB_SECTION.CROSS_ICON,
    sec_title: resolvedSubTitle,
    page_type: cmp ? headerConstants.SECTION_TITLES.SEARCH_RESULT : "",
  };

  const cleverTapObj = {
    component: data.component,
    event_click: data.event_click,
    target: data.target,
    cta_text: data.cta_text,
    search_keyword: data.search_keyword,
    index: String(data.index), // Convert to string for CleverTap
    sub_section: data.sub_section,
    sec_title: data.sec_title,
    page_type: data.page_type,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle search submit analytics
 * Logs click event for the search submit button
 */
export const handleSearchSubmitDL = (
  searchKeyword: string,
  cta_text: string = headerConstants.ELEMENTS.SEARCH_ICON,
  url: string = ""
) => {
  const data = {
    component: headerConstants.COMPONENTS.SEARCH_BAR,
    event_click: headerConstants.SEARCH.EVENT_CLICK.INPUT_TEXT,
    target: headerConstants.SEARCH.TARGET.SEARCH_BAR_INITIATION,
    cta_text: searchKeyword,
    ...(url ? { targetUrl: url } : {}),
  };

  const cleverTapObj = {
    component: data.component,
    event_click: data.event_click,
    target: data.target,
    cta_text: data.cta_text,
    ...(url ? { targetUrl: url } : {}),
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle search suggestion click analytics
 * Logs click event for selecting a search suggestion
 */
export const handleSearchSuggestionClickDL = (
  searchKeyword: string,
  isHistory: boolean,
  cta_text: string = headerConstants.ELEMENTS.SEARCH_SUGGESTION,
  url: string = "",
  sectionTitle: string | number = headerConstants.SECTION_TITLES.SEARCH,
  tabPosition: number = 0
) => {
  const resolvedSectionTitle =
    typeof sectionTitle === "number" ? cta_text : sectionTitle;
  const resolvedTabPosition =
    typeof sectionTitle === "number" ? sectionTitle : tabPosition;

  const data = {
    component: headerConstants.COMPONENTS.SEARCH_BANNER,
    event_click: headerConstants.SEARCH.EVENT_CLICK.OPTION_SELECTION,
    target: headerConstants.SEARCH.TARGET.SEARCH_BANNER_OPTION_SELECTION,
    targetUrl: url,
    cta_text: searchKeyword.trim(),
    search_keyword: searchKeyword.trim(),
    sec_title: resolvedSectionTitle,
    index: resolvedTabPosition,
    sub_section: searchKeyword.trim(),
    page_type: headerConstants.SECTION_TITLES.SEARCH_RESULT,
  };

  const cleverTapObj = {
    component: data.component,
    event_click: data.event_click,
    target: data.target,
    cta_text: data.cta_text,
    search_keyword: data.search_keyword,
    targetUrl: data.targetUrl,
    sec_title: data.sec_title,
    index: String(data.index), // Convert to string for CleverTap
    sub_section: data.sub_section,
    page_type: data.page_type,
  };

  dataLayer("", data, cleverTapObj);
};

/**
 * Handle search history delete icon click analytics
 * Logs click event for deleting a search history item
 */
export const handleSearchHistoryDeleteDL = (
  keyword: string,
  cmp: boolean = false,
  position: number = 0,
  subTitle: string = headerConstants.SECTION_TITLES.SEARCH_HISTORY
) => {
  const data = {
    component: cmp
      ? headerConstants.COMPONENTS.SEARCH_RESULT_PAGE
      : headerConstants.COMPONENTS.SEARCH_BANNER,
    event_click: headerConstants.SEARCH.EVENT_CLICK.ICON_CLICK,
    target: cmp
      ? headerConstants.SEARCH.TARGET.SEARCH_RESULT_REMOVE_OPTION
      : headerConstants.SEARCH.TARGET.SEARCH_BANNER_DIRECTION_ICON,
    cta_text: keyword.trim(),
    index: position !== 0 ? position : "NA",
    sub_section: headerConstants.SEARCH.SUB_SECTION.CROSS_ICON,
    sec_title: subTitle,
    page_type: cmp ? headerConstants.SECTION_TITLES.SEARCH_RESULT : "",
  };

  const cleverTapObj = {
    component: data.component,
    event_click: data.event_click,
    target: data.target,
    cta_text: data.cta_text,
    index: String(data.index), // Convert to string for CleverTap
    sub_section: data.sub_section,
    sec_title: data.sec_title,
    page_type: data.page_type,
  };

  dataLayer("", data, cleverTapObj);
};
